<template>
	<div id="lp-index" class="lp-index">
		<edit-toolbar @zoomFit="setZoomFit" />

		<panel-wrapper panelSide="left" :leftPanelSelected="leftPanelSelected">
			<left-panel
				:leftPanelsTop="leftPanelsTop"
				:leftPanelsBottom="leftPanelsBottom"
				:leftPanelSelected.sync="leftPanelSelected"
				@slideChanged="$emit('slideChanged', blocksSelector)"
			/>
		</panel-wrapper>

		<main
			class="lp-index-main"
			:style="{
				marginLeft: (leftPanelSelected > -1 ? 400 : 0) + 'px',
				marginRight: (rightPanelSelected > -1 ? lessonPlanController.rightPanelMargin : 0) + 'px'
			}"
		>
			<slide-time-line-wrapper></slide-time-line-wrapper>

			<footer-arrows
				@slideChanged="$emit('slideChanged', blocksSelector)"
				@prev="goToPrevSlide"
				@next="goToNextSlide"
				@update="goToSlideIndex"
				@toggleFullscreen="toggleFullscreen"
				@toggleSubtitles="toggleSubtitles"
				@selectDefaultLang="selectDefaultLang"
				:slide="lessonPlanController.navigation.selectedSlideIndex + 1"
				:total="lessonPlanController.navigation.slidesVisible.length"
				:can-go-next="true"
				:can-go-prev="true"
				:isFullscreen="lessonPlanController.isFullscreen"
				:subtitlesActive="lessonPlanController.subtitlesActive"
				:defaultSubtitles="lessonPlanController.defaultSubtitles"
			/>
			<block-canvas
				ref="canvas"
				:blocks="blocks"
				:key="slideId"
				:slide-id="slideId"
				:defaultBlockStyles="defaultBlockStyles"
				:settingStyles="settingStyles"
				@create:draft="$emit('create:draft', $event)"
				:globalVariables="globalVariables"
				class="p-block-designer-canvas"
				mode="edit"
			></block-canvas>
		</main>

		<panel-wrapper panelSide="right" :rightPanelSelected="rightPanelSelected">
			<right-panel
				:rightPanels="rightPanels"
				:rightPanelSelected.sync="rightPanelSelected"
				:key="slideId"
				@update:rightPanelSelected="handleUpdateRightPanelSelected"
				@update:rightPanels="handleUpdateRightPanels"
			/>
		</panel-wrapper>

		<modal-progress
			:show="lessonPlanController.progressModal.show"
			:title="lessonPlanController.progressModal.title"
		></modal-progress>
	</div>
</template>

<script>
import LeftPanel from '../../../../components/structure/LeftPanel.vue';
import RightPanel from '../../../../components/structure/RightPanel.vue';
import PanelWrapper from '../../../../components/structure/PanelWrapper.vue';
import EditToolbar from './EditToolbar.vue';
import BlockCanvas from '../../containers/BlockCanvas.vue';
import { createBlocksSelector } from '../../../../controllers/BlocksSelector/blocksSelectorFactory.js';
import Vue from 'vue';
import BlockCanvasGrid from '../../containers/BlockCanvasGrid.vue';
import FooterArrows from '../../../../../commonInteractive/navigation/FooterArrows.vue';
import { ModalProgress } from 'plant-common';
import helpers from '../../../../../../pages/content/_layout/DrawerReferences/helpers.vue';
import { cloneDeep } from '../../../../utils/utils';
import { ACTION_SET_CONTENT_REVISION, STORE_CONTENT_NAME } from '../../../../../../store_constants/content';
import { mapActions, mapState } from 'vuex';
import { STORE_LIBRARY_NAME } from '../../../../../../store_constants/library';
import { createBlockRemover } from '../../../../controllers/BlockRemover/blockRemoverFactory';
import { createBlocksZOrganizer } from '../../../../controllers/BlockMover/blocksZOrganizerFactory';
import { createSlideStyleData } from '../../../../controllers/SlideStyleData/slideStyleDataFactory';
import SlideTimeLineWrapper from './SlideTimeLineWrapper.vue';

export default {
	name: 'BlockDesigner',
	inject: ['lessonPlanController', 'DependencyBus'],
	mixins: [helpers],
	components: {
		SlideTimeLineWrapper,
		LeftPanel,
		RightPanel,
		PanelWrapper,
		EditToolbar,
		BlockCanvas,
		BlockCanvasGrid,
		FooterArrows,
		ModalProgress
	},
	provide() {
		return {
			blockDefinitions: this.blockDefinitions,
			blocksSelector: this.blocksSelector,
			blockRemover: this.blockRemover,
			blocksZOrganizer: this.blocksZOrganizer,
			slideStyleData: this.slideStyleData,
			// blockEventBus in edit mode to avoid error in console when using inject
			blockEventBus: new Vue()
		};
	},
	props: {
		blocks: Array,
		slideId: String,
		blockDefinitions: Object,
		defaultBlockStyles: Object,
		settingStyles: Object,
		leftPanelsTop: Array,
		leftPanelsBottom: Array,
		rightPanels: Array,
		isBlockSelected: Boolean,
		gridSize: Array,
		availableStencils: Array,
		globalVariables: Array
	},
	data() {
		const blocksSelector = createBlocksSelector(this);
		return {
			leftPanelSelected: -1,
			rightPanelSelected: -1,
			currentRightPanels: null,
			blocksSelector: blocksSelector,
			blockRemover: createBlockRemover(this),
			blocksZOrganizer: createBlocksZOrganizer(this, blocksSelector),
			slideStyleData: createSlideStyleData(this)
		};
	},
	created() {
		this.DependencyBus.$on('updateDependencyFromLibraryContent', this.updateDependencyFromLibraryContent);
	},
	computed: {
		...mapState(STORE_LIBRARY_NAME, ['settings'])
	},
	watch: {
		blocks: {
			deep: true,
			handler() {
				this.blocksSelector.blocks = this.blocks;
				this.blockRemover.blocks = this.blocks;
				this.blocksZOrganizer.blocks = this.blocks;
			}
		},
		gridSize: {
			deep: true,
			handler() {
				this.slideStyleData.gridSize = this.gridSize;
			}
		},
		availableStencils: {
			deep: true,
			handler() {
				this.slideStyleData.availableStencils = this.availableStencils;
			}
		},
		rightPanelSelected: {
			handler() {
				if (this.rightPanelSelected === -1) return;

				this.updateLastRightPanelTitle(this.rightPanelSelected);
			}
		}
	},
	methods: {
		...mapActions(STORE_CONTENT_NAME, [ACTION_SET_CONTENT_REVISION]),
		async updateDependencyFromLibraryContent(dependency) {
			const contents = cloneDeep(this.lessonPlanController.lessonPlanContents);
			const selectedLibraryURL = this.settings.url;

			await this.updateContentToLatestRevision(contents, [dependency], selectedLibraryURL);

			await this[ACTION_SET_CONTENT_REVISION](contents);
		},
		setZoomFit() {
			this.$nextTick(() => {
				this.$refs['canvas'].zoomFit();
			});
		},
		goToPrevSlide() {
			this.lessonPlanController.navigation.prevSlide();
		},
		goToNextSlide() {
			this.lessonPlanController.navigation.nextSlide();
		},
		goToSlideIndex(number) {
			this.lessonPlanController.navigation.selectSlideByIndex(number - 1);
		},
		toggleFullscreen() {
			this.lessonPlanController.toggleFullscreen();
		},
		toggleSubtitles() {
			this.lessonPlanController.toggleSubtitles();
		},
		selectDefaultLang(lang) {
			this.lessonPlanController.selectDefaultLang(lang);
		},
		handleUpdateRightPanels(panels) {
			const currentRightPanelsStr = JSON.stringify(this.currentRightPanels);
			const panelsStr = JSON.stringify(panels);

			if (currentRightPanelsStr === panelsStr) return;

			this.currentRightPanels = panels;

			this.validateRightPanelSelected();
		},
		handleUpdateRightPanelSelected(index) {
			if (index === -1) this.lessonPlanController.lastRightPanelSelectedTitle = null;
		},
		validateRightPanelSelected() {
			const selectedPanelIndex = this.currentRightPanels.findIndex(
				panel => panel.title === this.lessonPlanController.lastRightPanelSelectedTitle
			);

			this.rightPanelSelected = selectedPanelIndex;
		},
		updateLastRightPanelTitle(index) {
			const panel = this.currentRightPanels[index];

			this.lessonPlanController.lastRightPanelSelectedTitle = panel.title;
		}
	}
};
</script>

<style>
/* Display complete canvas with footer */
.p-block-designer-canvas.lp-slide-wrapper,
.p-block-designer-canvas .lp-slide {
	bottom: 35px;
}
</style>
